import { FormEvent, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useAppDispatch } from '@/store';
import { loginWithGoogle, registerUser } from '@/store/slices/authSlice';
import { GoogleSignInButton } from '@/components/GoogleSignInButton';
import { useRedirectTarget } from '@/hooks/useRedirectTarget';

export function RegisterPage(): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { status, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [googleError, setGoogleError] = useState<string | null>(null);

  const redirectTo = useRedirectTarget();
  const from = (location.state as { from?: unknown } | null)?.from;

  const isLoading = status === 'loading';

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setLocalError(null);
    setGoogleError(null);
    if (password.length < 8) {
      setLocalError(t('auth.register.passwordTooShort'));
      return;
    }
    if (password !== confirm) {
      setLocalError(t('auth.register.passwordMismatch'));
      return;
    }
    const result = await dispatch(
      registerUser({ email, password, displayName: displayName || undefined }),
    );
    if (registerUser.fulfilled.match(result)) {
      navigate(redirectTo, { replace: true });
    }
  };

  const handleGoogle = async (credential: string): Promise<void> => {
    setGoogleError(null);
    const result = await dispatch(loginWithGoogle(credential));
    if (loginWithGoogle.fulfilled.match(result)) {
      navigate(redirectTo, { replace: true });
    }
  };

  // Precedence: local validation > Google SDK failure > slice/auth error.
  const shownError = localError ?? googleError ?? error;

  return (
    <main className="auth-page">
      <div className="auth-card">
        <h1>{t('auth.register.title')}</h1>
        <p className="muted">{t('auth.register.blurb')}</p>

        {shownError && (
          <div className="error" role="alert">
            {shownError}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="field">
            <label htmlFor="displayName">{t('auth.register.displayName')}</label>
            <input
              id="displayName"
              autoComplete="nickname"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="email">{t('auth.email')}</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="password">{t('auth.password')}</label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
          </div>

          <div className="field">
            <label htmlFor="confirm">{t('auth.register.confirmPassword')}</label>
            <input
              id="confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
            />
          </div>

          <button type="submit" className="btn" disabled={isLoading}>
            {isLoading ? t('auth.register.submitting') : t('auth.register.submit')}
          </button>
        </form>

        <div className="divider">{t('auth.or')}</div>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <GoogleSignInButton
            onCredential={handleGoogle}
            onError={() => setGoogleError(t('auth.googleError'))}
          />
        </div>

        <div className="footer-link">
          <Trans
            i18nKey="auth.register.footer"
            components={{ link: <Link to="/login" state={from ? { from } : undefined} /> }}
          />
        </div>
      </div>
    </main>
  );
}
